import { formatMoney, PORTAL_WORK_STATES, type PortalDto, type PortalInvoiceDto, type PortalProtocolDto, type PortalWorkDto, portalWorkDays, todayInWarsaw } from '@bqcrm/shared';
import { ActionIcon, Alert, Badge, Box, Button, Card, CopyButton, Group, Paper, Select, SimpleGrid, Stack, Tabs, Text, Title, Tooltip, UnstyledButton } from '@mantine/core';
import { IconCalendarEvent, IconCheck, IconChevronLeft, IconChevronRight, IconCopy, IconCreditCard, IconFileTypePdf, IconMail, IconPhone, IconSignature } from '@tabler/icons-react';
import { useMediaQuery } from '@mantine/hooks';
import { createContext, useContext, useMemo, useState } from 'react';
import { api, errorText } from '../../api';
import { dayLong, dayRange, plural } from '../../format';
import { PortalMessages } from './PortalMessages';
import { ChangeRequest } from './ChangeRequest';
import { rememberPortalReturn } from './portalReturn';

/** Numery spraw klienta — znacznik sprawy przy pozycjach, gdy klient ma kilka spraw (docs/18). */
const CaseNumbers = createContext<Map<string, string> | null>(null);
/** Znacznik sprawy (tylko przy kilku sprawach w strefie). */
export function CaseTag({ id }: { id: string | undefined }) {
  const map = useContext(CaseNumbers);
  const n = id ? map?.get(id) : undefined;
  if (!n) return null;
  return (
    <Badge size="xs" variant="outline" color="gray" style={{ verticalAlign: 'middle' }}>
      {n}
    </Badge>
  );
}

/** Odnośniki do plików (strona klienta); w podglądzie biura brak — przyciski nieaktywne. */
export interface PortalFiles {
  offer: (versionId: string) => string;
  contract: (id: string) => string;
  signed: (id: string) => string;
  invoice: (id: string) => string;
  /** protokół odbioru: podpisany PDF albo protokół do podpisu */
  protocol: (id: string) => string;
  /** skan podpisanego wydruku protokołu */
  protocolScan: (id: string) => string;
  /** adres strefy w API (`/public/strefa/<token>`) — zakładka Wiadomości; w podglądzie biura brak */
  base?: string;
}

// odcienie 8 z białym napisem — czytelne na każdym z tych kolorów
const PALETTE = ['blue', 'teal', 'grape', 'orange', 'cyan', 'pink', 'indigo', 'violet'] as const;
const WEEKDAYS = ['pon.', 'wt.', 'śr.', 'czw.', 'pt.', 'sob.', 'niedz.'];
const MONTHS = ['styczeń', 'luty', 'marzec', 'kwiecień', 'maj', 'czerwiec', 'lipiec', 'sierpień', 'wrzesień', 'październik', 'listopad', 'grudzień'];

const noon = (d: string) => new Date(`${d}T12:00:00Z`);
const iso = (d: Date) => d.toISOString().slice(0, 10);
const monthOf = (d: string) => d.slice(0, 7);
const shiftMonth = (m: string, n: number) => {
  const d = noon(`${m}-15`);
  d.setUTCMonth(d.getUTCMonth() + n);
  return iso(d).slice(0, 7);
};
const STATE_COLOR: Record<PortalWorkDto['state'], string> = { zaplanowane: 'blue', w_toku: 'green', wstrzymane: 'orange', zakonczone: 'gray' };

/** Kolor pracy: stały dla tytułu (kolejność pierwszego wystąpienia), zakończone — szare. */
function useColors(works: PortalWorkDto[]) {
  return useMemo(() => {
    const titles = [...new Set(works.map((w) => w.title))];
    return (w: PortalWorkDto) => (w.state === 'zakonczone' ? 'gray' : PALETTE[titles.indexOf(w.title) % PALETTE.length]!);
  }, [works]);
}

interface WeekBar {
  w: PortalWorkDto;
  /** kolumna startu w tygodniu (0 = poniedziałek) i liczba dni */
  col: number;
  span: number;
  lane: number;
  /** słupek zaczyna / kończy pracę (a nie jest jej ciągiem z poprzedniego / w następnym tygodniu) */
  startsHere: boolean;
  endsHere: boolean;
}

/** Słupki tygodnia: każdy ciąg kolejnych dni pracy to jeden słupek; słupki rozłożone na wiersze bez nakładania. */
export function weekBars(week: string[], works: PortalWorkDto[]): WeekBar[] {
  const runs: Omit<WeekBar, 'lane'>[] = [];
  for (const w of works) {
    const days = new Set(portalWorkDays(w));
    let i = 0;
    while (i < 7) {
      if (!days.has(week[i]!)) {
        i++;
        continue;
      }
      let j = i;
      while (j + 1 < 7 && days.has(week[j + 1]!)) j++;
      runs.push({ w, col: i, span: j - i + 1, startsHere: week[i] === w.dateFrom, endsHere: week[j] === w.dateTo });
      i = j + 1;
    }
  }
  runs.sort((a, b) => a.col - b.col || b.span - a.span);
  const laneEnd: number[] = [];
  return runs.map((r) => {
    let lane = laneEnd.findIndex((end) => end < r.col);
    if (lane < 0) lane = laneEnd.length;
    laneEnd[lane] = r.col + r.span - 1;
    return { ...r, lane };
  });
}

function MonthCalendar({ works, colorOf, base }: { works: PortalWorkDto[]; colorOf: (w: PortalWorkDto) => string; base?: string }) {
  const narrow = useMediaQuery('(max-width: 36em)');
  const today = todayInWarsaw();
  const byDay = useMemo(() => {
    const m = new Map<string, PortalWorkDto[]>();
    for (const w of works) for (const d of portalWorkDays(w)) m.set(d, [...(m.get(d) ?? []), w]);
    return m;
  }, [works]);
  // start: miesiąc najbliższej (trwającej lub przyszłej) pracy, inaczej bieżący
  const initial = monthOf(works.find((w) => w.dateTo >= today)?.dateFrom ?? today);
  const [month, setMonth] = useState(initial < monthOf(today) ? monthOf(today) : initial);
  const [picked, setPicked] = useState<string | null>(null);
  const first = noon(`${month}-01`);
  const lead = (first.getUTCDay() + 6) % 7; // poniedziałek = 0
  const start = new Date(first);
  start.setUTCDate(1 - lead);
  const days: string[] = [];
  for (let i = 0; i < 42; i++) {
    const d = new Date(start);
    d.setUTCDate(start.getUTCDate() + i);
    days.push(iso(d));
  }
  const weeks = days.slice(35).every((d) => monthOf(d) !== month) ? 5 : 6;
  const pickedWorks = picked ? (byDay.get(picked) ?? []) : [];
  return (
    <Card withBorder radius="md" padding="sm">
      <Group justify="space-between" mb="xs">
        <ActionIcon variant="subtle" aria-label="Poprzedni miesiąc" onClick={() => setMonth(shiftMonth(month, -1))}>
          <IconChevronLeft size={18} />
        </ActionIcon>
        <Text fw={700} tt="capitalize">
          {MONTHS[Number(month.slice(5, 7)) - 1]} {month.slice(0, 4)}
        </Text>
        <ActionIcon variant="subtle" aria-label="Następny miesiąc" onClick={() => setMonth(shiftMonth(month, 1))}>
          <IconChevronRight size={18} />
        </ActionIcon>
      </Group>
      <Box style={{ display: 'grid', gridTemplateColumns: 'repeat(7, minmax(0, 1fr))', gap: 2 }}>
        {WEEKDAYS.map((w) => (
          <Text key={w} size="xs" c="dimmed" ta="center" fw={600} pb={4}>
            {w}
          </Text>
        ))}
      </Box>
      <Stack gap={2}>
        {Array.from({ length: weeks }, (_, i) => days.slice(i * 7, i * 7 + 7)).map((week) => {
          const bars = weekBars(week, works);
          const lanes = bars.reduce((m, b) => Math.max(m, b.lane + 1), 0);
          const laneH = narrow ? 8 : 44;
          return (
            <Box key={week[0]} pos="relative" style={{ minHeight: Math.max(narrow ? 48 : 74, 24 + lanes * (laneH + 3) + 6) }}>
              {/* tło: dni (klik — szczegóły dnia pod kalendarzem) */}
              <Box style={{ position: 'absolute', inset: 0, display: 'grid', gridTemplateColumns: 'repeat(7, minmax(0, 1fr))', gap: 2 }}>
                {week.map((d) => {
                  const list = byDay.get(d) ?? [];
                  const isToday = d === today;
                  return (
                    <UnstyledButton
                      key={d}
                      onClick={() => setPicked(picked === d ? null : d)}
                      aria-label={`${dayLong(d)}${list.length ? `: ${list.map((w) => w.title).join(', ')}` : ''}`}
                      style={{
                        padding: 4,
                        borderRadius: 6,
                        border: `1px solid ${picked === d ? 'var(--mantine-color-eko-6)' : 'var(--mantine-color-default-border)'}`,
                        background: monthOf(d) !== month ? 'var(--app-subtle)' : 'var(--mantine-color-body)',
                        alignItems: 'flex-start',
                        display: 'flex',
                      }}
                    >
                      <Text size="xs" fw={isToday ? 800 : 500} c={isToday ? 'eko.7' : monthOf(d) !== month ? 'dimmed' : undefined}>
                        {noon(d).getUTCDate()}
                        {isToday ? ' · dziś' : ''}
                      </Text>
                    </UnstyledButton>
                  );
                })}
              </Box>
              {/* słupki: praca w kolejnych dniach — jeden słupek (przerwa w pracy albo koniec tygodnia go dzieli) */}
              <Box
                style={{
                  position: 'absolute',
                  inset: '24px 0 0 0',
                  display: 'grid',
                  gridTemplateColumns: 'repeat(7, minmax(0, 1fr))',
                  gridAutoRows: laneH,
                  rowGap: 3,
                  columnGap: 2,
                  pointerEvents: 'none',
                }}
              >
                {bars.map((b) => (
                  <Box
                    key={`${b.w.id}:${b.col}`}
                    title={b.w.title}
                    style={{
                      gridColumn: `${b.col + 1} / span ${b.span}`,
                      gridRow: b.lane + 1,
                      margin: `0 ${b.endsHere ? 3 : 0}px 0 ${b.startsHere ? 3 : 0}px`,
                      // jak wpis w Kalendarzu biura: pełny kolor, dwa rzędy, ta sama wysokość
                      background: `var(--mantine-color-${colorOf(b.w)}-8)`,
                      color: 'var(--mantine-color-white)',
                      borderRadius: `${b.startsHere ? 5 : 0}px ${b.endsHere ? 5 : 0}px ${b.endsHere ? 5 : 0}px ${b.startsHere ? 5 : 0}px`,
                      padding: narrow ? 0 : '4px 8px',
                      overflow: 'hidden',
                      opacity: monthOf(week[b.col]!) !== month ? 0.6 : 1,
                    }}
                  >
                    <Text size="sm" lh="20px" truncate visibleFrom="sm" fw={600}>
                      {b.w.title}
                    </Text>
                    <Text size="xs" lh="16px" truncate visibleFrom="sm" style={{ opacity: 0.85 }}>
                      {[b.w.trades.join(', '), b.w.part ? `etap ${b.w.part.no} z ${b.w.part.of}` : null].filter(Boolean).join(' · ') || PORTAL_WORK_STATES[b.w.state]}
                    </Text>
                  </Box>
                ))}
              </Box>
            </Box>
          );
        })}
      </Stack>
      {picked && (
        <Box mt="sm">
          <Text size="sm" fw={700}>
            {dayLong(picked)}
          </Text>
          {pickedWorks.length ? (
            pickedWorks.map((w) => (
              <Box key={w.id} mb={6}>
                <Text size="sm">
                  <Box component="span" display="inline-block" w={10} h={10} mr={6} style={{ borderRadius: 2, background: `var(--mantine-color-${colorOf(w)}-6)` }} />
                  {w.title}
                  {w.trades.length ? <Text span c="dimmed">{` · ${w.trades.join(', ')}`}</Text> : null}
                </Text>
                {/* prośba dotyczy całej pracy — przy pierwszym etapie */}
                {(!w.part || w.part.no === 1) && <ChangeRequest work={w} base={base} />}
              </Box>
            ))
          ) : (
            <Text size="sm" c="dimmed">
              Tego dnia nie planujemy prac.
            </Text>
          )}
        </Box>
      )}
    </Card>
  );
}

function WorkList({ works, colorOf, unscheduled, base }: { works: PortalWorkDto[]; colorOf: (w: PortalWorkDto) => string; unscheduled: number; base?: string }) {
  const today = todayInWarsaw();
  const nextId = works.find((w) => w.dateTo >= today && w.state !== 'zakonczone')?.id;
  return (
    <Stack gap="xs">
      {works.map((w) => {
        const past = w.state === 'zakonczone' || w.dateTo < today;
        return (
          <Card key={w.id} withBorder radius="md" padding="sm" style={{ borderLeft: `4px solid var(--mantine-color-${colorOf(w)}-6)`, opacity: past ? 0.7 : 1 }}>
            <Group justify="space-between" wrap="wrap" gap="xs">
              <div style={{ flex: '1 1 220px' }}>
                <Group gap={6}>
                  <Text fw={700}>{w.title}</Text>
                  <CaseTag id={w.caseId} />
                  {w.part && (
                    <Text size="sm" c="dimmed">
                      etap {w.part.no} z {w.part.of}
                    </Text>
                  )}
                  {w.id === nextId && (
                    <Badge size="sm" variant="light" color="eko">
                      Najbliższa praca
                    </Badge>
                  )}
                </Group>
                {w.trades.length > 0 && (
                  <Text size="sm" c="dimmed">
                    {w.trades.join(', ')}
                  </Text>
                )}
                {(!w.part || w.part.no === 1) && <ChangeRequest work={w} base={base} />}
              </div>
              <Stack gap={2} align="flex-end">
                <Group gap={6} wrap="nowrap">
                  <IconCalendarEvent size={16} style={{ opacity: 0.7 }} />
                  <Text size="sm" fw={600}>
                    {w.dateFrom === w.dateTo ? dayLong(w.dateFrom) : `Start: ${dayLong(w.dateFrom)}`}
                  </Text>
                </Group>
                {w.dateFrom !== w.dateTo && (
                  <Text size="xs" c="dimmed">
                    {dayRange(w.dateFrom, w.dateTo)} · {portalWorkDays(w).length} {plural(portalWorkDays(w).length, 'dzień pracy', 'dni pracy', 'dni pracy')}
                  </Text>
                )}
                <Badge size="sm" variant="light" color={STATE_COLOR[w.state]}>
                  {PORTAL_WORK_STATES[w.state]}
                </Badge>
              </Stack>
            </Group>
          </Card>
        );
      })}
      {unscheduled > 0 && (
        <Text size="sm" c="dimmed">
          Prace bez ustalonego terminu: {unscheduled} — pojawią się tutaj po zaplanowaniu.
        </Text>
      )}
    </Stack>
  );
}

function PdfButton({ href, label, icon }: { href: string | null; label: string; icon?: React.ReactNode }) {
  return href ? (
    <Button size="xs" variant="default" leftSection={icon ?? <IconFileTypePdf size={16} />} component="a" href={href} target="_blank">
      {label}
    </Button>
  ) : (
    <Button size="xs" variant="default" leftSection={icon ?? <IconFileTypePdf size={16} />} disabled title="W podglądzie biura pobieranie jest wyłączone">
      {label}
    </Button>
  );
}

function Documents({ portal, files }: { portal: PortalDto; files: PortalFiles | null }) {
  return (
    <Stack>
      <Title order={4}>Umowy i aneksy</Title>
      {portal.contracts.length ? (
        portal.contracts.map((c) => (
          <Card key={c.id} withBorder radius="md" padding="sm">
            <Group justify="space-between" wrap="wrap" gap="xs">
              <div style={{ flex: '1 1 220px' }}>
                <Text fw={700}>
                  {c.kind === 'aneks' ? 'Aneks' : 'Umowa'} {c.number} <CaseTag id={c.caseId} />
                </Text>
                <Text size="sm" c="dimmed">
                  {c.title}
                  {c.status === 'podpisana' && c.signedAt ? ` · podpisana ${dayLong(c.signedAt, { weekday: false })}` : ''}
                </Text>
              </div>
              <Group gap="xs">
                <Badge size="sm" variant="light" color={c.status === 'podpisana' ? 'green' : 'blue'}>
                  {c.status === 'podpisana' ? 'Podpisana' : 'Do podpisu'}
                </Badge>
                {c.signedFile ? (
                  <PdfButton href={files?.signed(c.id) ?? null} label={c.signedFile === 'epodpis' ? 'Pobierz podpisany dokument' : 'Pobierz skan podpisanej umowy'} icon={<IconSignature size={16} />} />
                ) : null}
                <PdfButton href={files?.contract(c.id) ?? null} label="Pobierz treść (PDF)" />
              </Group>
            </Group>
          </Card>
        ))
      ) : (
        <Text size="sm" c="dimmed">
          Brak umów do wyświetlenia.
        </Text>
      )}
      <Title order={4} mt="md">
        Oferty
      </Title>
      {portal.offers.length ? (
        portal.offers.map((o) => (
          <Card key={o.versionId} withBorder radius="md" padding="sm">
            <Group justify="space-between" wrap="wrap" gap="xs">
              <div style={{ flex: '1 1 220px' }}>
                <Text fw={700}>
                  Oferta {o.number} <CaseTag id={o.caseId} />
                </Text>
                <Text size="sm" c="dimmed">
                  {o.title}
                  {o.grossGr != null ? ` · ${formatMoney(o.grossGr)} brutto` : ''}
                </Text>
              </div>
              <Group gap="xs">
                <Badge size="sm" variant="light" color={o.status === 'zaakceptowana' ? 'green' : o.status === 'aktywna' ? 'blue' : 'gray'}>
                  {o.status === 'zaakceptowana' ? 'Zaakceptowana' : o.status === 'aktywna' ? `Ważna do ${dayLong(o.validUntil, { weekday: false })}` : 'Nieaktualna'}
                </Badge>
                <PdfButton href={files?.offer(o.versionId) ?? null} label="Pobierz PDF" />
              </Group>
            </Group>
          </Card>
        ))
      ) : (
        <Text size="sm" c="dimmed">
          Brak ofert do wyświetlenia.
        </Text>
      )}
    </Stack>
  );
}

function InvoiceStateBadge({ inv }: { inv: PortalInvoiceDto }) {
  if (inv.state === 'korekta') return null;
  const toPay = inv.toPayGr != null ? formatMoney(inv.toPayGr) : '';
  const [color, label] =
    inv.state === 'oplacona' ? ['green', 'Opłacona'] : inv.state === 'po_terminie' ? ['red', `Po terminie · do zapłaty ${toPay}`] : ['blue', `Do zapłaty ${toPay}`];
  return (
    <Badge size="sm" variant="light" color={color}>
      {label}
    </Badge>
  );
}

function CopyLine({ label, value }: { label: string; value: string }) {
  return (
    <Group gap={4} wrap="nowrap" align="flex-start">
      <Text size="sm" style={{ wordBreak: 'break-word' }}>
        <Text span c="dimmed">
          {label}:{' '}
        </Text>
        {value}
      </Text>
      <CopyButton value={value} timeout={1500}>
        {({ copied, copy }) => (
          <Tooltip label={copied ? 'Skopiowano' : 'Kopiuj'} withArrow>
            <ActionIcon size="sm" variant="subtle" color={copied ? 'green' : 'gray'} onClick={copy} aria-label={`Kopiuj: ${label}`}>
              {copied ? <IconCheck size={14} /> : <IconCopy size={14} />}
            </ActionIcon>
          </Tooltip>
        )}
      </CopyButton>
    </Group>
  );
}

/** Protokoły odbioru prac: podpisane (PDF, skan wydruku) i czekające na podpis klienta (e-podpis z linku albo wydruk). */
function Protocols({ protocols, files }: { protocols: PortalProtocolDto[]; files: PortalFiles | null }) {
  return (
    <Stack gap="xs">
      {protocols.map((p) => {
        const pending = p.state === 'do_podpisu';
        const how = p.signMethod === 'epodpis' ? 'elektronicznie' : p.signMethod === 'papier' ? 'na papierze' : 'na miejscu';
        return (
          <Card key={p.id} withBorder radius="md" padding="sm">
            <Group justify="space-between" wrap="wrap" gap="xs" align="flex-start">
              <div style={{ flex: '1 1 240px' }}>
                <Text fw={700}>
                  {p.title} {p.code} <CaseTag id={p.caseId} />
                </Text>
                <Text size="sm" c="dimmed">
                  {pending
                    ? `przygotowany ${dayLong(p.date.slice(0, 10), { weekday: false })} · podpisuje ${p.signerName}`
                    : `podpisany ${how} ${dayLong(p.date.slice(0, 10), { weekday: false })} · ${p.signerName}`}
                </Text>
                {pending && p.esign && (
                  <Text size="sm" mt={4}>
                    {p.esign.expired
                      ? 'Link do podpisu wygasł — skontaktuj się z nami, wyślemy nowy.'
                      : `Link do podpisu elektronicznego wysłaliśmy na adres ${p.esign.sentTo} (ważny do ${dayLong(p.esign.expiresAt.slice(0, 10), { weekday: false })}).`}
                  </Text>
                )}
                {pending && p.signMethod === 'papier' && (
                  <Text size="sm" mt={4}>
                    Protokół do podpisu na wydruku: pobierz PDF, podpisz i odeślij nam skan lub zdjęcie (albo przekaż podpisany wydruk ekipie).
                  </Text>
                )}
                {p.remarks && (
                  <Text size="sm" mt={4}>
                    Zgłoszone uwagi: „{p.remarks}”
                  </Text>
                )}
              </div>
              <Group gap="xs">
                <Badge size="sm" variant="light" color={pending ? 'orange' : 'green'}>
                  {pending ? 'Czeka na Twój podpis' : 'Podpisany'}
                </Badge>
                {p.scan && <PdfButton href={files?.protocolScan(p.id) ?? null} label="Pobierz skan podpisanego protokołu" icon={<IconSignature size={16} />} />}
                {p.pdf && <PdfButton href={files?.protocol(p.id) ?? null} label={pending ? 'Pobierz protokół do podpisu' : 'Pobierz protokół (PDF)'} />}
              </Group>
            </Group>
          </Card>
        );
      })}
    </Stack>
  );
}

/** Dokumenty finansowe: faktury sprawy z PDF, stanem płatności i danymi do przelewu (jak w e-mailu z fakturą). */
/**
 * docs/24: „Zapłać online” — płatność w bramce ING Pay na kwotę pozostałą do zapłaty (wylicza ją serwer); po zapłacie
 * klient wraca na stronę płatności, a wpłata pojawi się tu po potwierdzeniu z banku. W podglądzie biura nieaktywny.
 */
function PayOnlineButton({ inv, base }: { inv: PortalInvoiceDto; base: string | undefined }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const pay = async () => {
    if (!base) return;
    setBusy(true);
    setError(null);
    try {
      const r = await api<{ url: string }>(`${base}/faktury/${inv.id}/zaplac`, { method: 'POST' });
      rememberPortalReturn();
      window.location.assign(r.url);
    } catch (e) {
      setError(errorText(e));
      setBusy(false);
    }
  };
  return (
    <Stack gap={4} align="flex-end">
      <Button size="xs" leftSection={<IconCreditCard size={16} />} onClick={() => void pay()} loading={busy} disabled={!base} title={base ? undefined : 'W podglądzie biura płatność jest wyłączona'}>
        Zapłać online{inv.toPayGr != null ? ` ${formatMoney(inv.toPayGr)}` : ''}
      </Button>
      {error && (
        <Alert color="red" p={6} style={{ maxWidth: 320 }}>
          <Text size="xs">{error}</Text>
        </Alert>
      )}
    </Stack>
  );
}

function FinanceDocuments({ invoices, files }: { invoices: PortalInvoiceDto[]; files: PortalFiles | null }) {
  if (!invoices.length) {
    return (
      <Text size="sm" c="dimmed">
        Brak dokumentów finansowych. Faktury pojawią się tutaj po wystawieniu.
      </Text>
    );
  }
  return (
    <Stack gap="xs">
      {invoices.map((inv) => (
        <Card key={inv.id} withBorder radius="md" padding="sm">
          <Group justify="space-between" wrap="wrap" gap="xs" align="flex-start">
            <div style={{ flex: '1 1 260px' }}>
              <Text fw={700}>
                {inv.kindLabel} {inv.number} <CaseTag id={inv.caseId} />
              </Text>
              <Text size="sm" c="dimmed">
                {[
                  inv.issueDate ? `wystawiona ${dayLong(inv.issueDate, { weekday: false })}` : null,
                  inv.grossGr != null ? `${formatMoney(inv.grossGr)} brutto` : null,
                  inv.paymentTo && inv.state !== 'korekta' ? `termin płatności ${dayLong(inv.paymentTo, { weekday: false })}` : null,
                  inv.correctedNumber ? `korekta faktury ${inv.correctedNumber}` : null,
                  inv.proformaNumber ? `zastępuje pro formę ${inv.proformaNumber}` : null,
                ]
                  .filter(Boolean)
                  .join(' · ')}
              </Text>
            </div>
            <Group gap="xs">
              <InvoiceStateBadge inv={inv} />
              <PdfButton href={files?.invoice(inv.id) ?? null} label="Pobierz PDF" />
            </Group>
          </Group>
          {inv.payOnline && (
            <Group justify="flex-end" mt="xs">
              <Text size="xs" c="dimmed">
                BLIK, karta, szybki przelew
              </Text>
              <PayOnlineButton inv={inv} base={files?.base} />
            </Group>
          )}
          {inv.transfer && (
            <Paper withBorder radius="sm" p="xs" mt="xs" bg="var(--app-subtle)">
              <Text size="sm" fw={600} mb={2}>
                Dane do przelewu
              </Text>
              {inv.transfer.account ? <CopyLine label="Rachunek" value={inv.transfer.account} /> : null}
              {inv.transfer.recipient ? (
                <Text size="sm">
                  <Text span c="dimmed">
                    Odbiorca:{' '}
                  </Text>
                  {inv.transfer.recipient}
                </Text>
              ) : null}
              <CopyLine label="Tytuł przelewu" value={inv.transfer.title} />
              {inv.toPayGr != null && (
                <Text size="sm">
                  <Text span c="dimmed">
                    Kwota:{' '}
                  </Text>
                  {formatMoney(inv.toPayGr)}
                </Text>
              )}
            </Paper>
          )}
        </Card>
      ))}
      <Text size="xs" c="dimmed">
        Stan płatności aktualizujemy po zaksięgowaniu wpłaty — przelew wykonany dziś może być widoczny z opóźnieniem.
      </Text>
    </Stack>
  );
}

/** Strefa klienta: harmonogram prac (kalendarz i lista) oraz umowy i oferty — strona klienta i podgląd w biurze. */
export function PortalView({ portal: all, files }: { portal: PortalDto; files: PortalFiles | null }) {
  // filtr „Sprawa”: domyślnie wszystkie sprawy klienta
  const [caseId, setCaseId] = useState<string | null>(null);
  const many = all.cases.length > 1;
  const pick = <T extends { caseId?: string }>(xs: T[]) => (caseId ? xs.filter((x) => x.caseId === caseId) : xs);
  const portal: PortalDto = { ...all, works: pick(all.works), contracts: pick(all.contracts), offers: pick(all.offers), invoices: pick(all.invoices), protocols: pick(all.protocols ?? []) };
  const shownCases = caseId ? all.cases.filter((c) => c.id === caseId) : all.cases;
  const unscheduled = shownCases.reduce((n, c) => n + c.unscheduled, 0);
  const numbers = useMemo(() => (many ? new Map(all.cases.map((c) => [c.id, c.number])) : new Map<string, string>()), [all.cases, many]);
  const colorOf = useColors(all.works);
  return (
    <CaseNumbers.Provider value={numbers}>
    <Stack>
      <Box h={6} bg={portal.company.color} style={{ borderRadius: 3 }} />
      <Group justify="space-between" align="flex-start" wrap="wrap">
        <div>
          <Text c="dimmed" size="sm">
            {portal.company.name} · strefa klienta
          </Text>
          <Title order={2}>{portal.clientName}</Title>
          <Text c="dimmed">
            {all.cases.length ? `Twoje sprawy: ${all.cases.length} — harmonogram prac i dokumenty ze wszystkich spraw` : 'Nie masz jeszcze spraw — tutaj pojawią się prace i dokumenty.'}
          </Text>
          {many && (
            <Select
              mt="xs"
              w={{ base: '100%', sm: 360 }}
              label="Sprawa"
              value={caseId ?? ''}
              onChange={(v) => setCaseId(v || null)}
              allowDeselect={false}
              data={[{ value: '', label: 'Wszystkie sprawy' }, ...all.cases.map((c) => ({ value: c.id, label: `${c.number} — ${c.title}${c.closed ? ' (zakończona)' : ''}` }))]}
            />
          )}
        </div>
        <Card withBorder radius="md" padding="sm" w={{ base: '100%', sm: 300 }}>
          <Text size="sm" fw={700}>
            Kontakt w sprawie terminów
          </Text>
          {portal.contact && <Text size="sm">{portal.contact.name}</Text>}
          {[portal.contact?.phone || portal.company.phone].filter(Boolean).map((p) => (
            <Group key={p} gap={6}>
              <IconPhone size={14} />
              <Text size="sm" component="a" href={`tel:${p.replace(/\s/g, '')}`}>
                {p}
              </Text>
            </Group>
          ))}
          {[portal.contact?.email || portal.company.email].filter(Boolean).map((m) => (
            <Group key={m} gap={6}>
              <IconMail size={14} />
              <Text size="sm" component="a" href={`mailto:${m}`}>
                {m}
              </Text>
            </Group>
          ))}
        </Card>
      </Group>
      <Tabs defaultValue="harmonogram" keepMounted={false}>
        <Tabs.List>
          <Tabs.Tab value="harmonogram">Harmonogram prac</Tabs.Tab>
          <Tabs.Tab value="sprawy">Sprawy ({shownCases.length})</Tabs.Tab>
          <Tabs.Tab value="dokumenty">Umowy i oferty</Tabs.Tab>
          {portal.protocols?.length ? <Tabs.Tab value="protokoly">Protokoły odbioru</Tabs.Tab> : null}
          <Tabs.Tab value="finanse">Dokumenty finansowe</Tabs.Tab>
          {files?.base ? (
            <Tabs.Tab
              value="wiadomosci"
              rightSection={
                all.unreadMessages ? (
                  <Badge size="sm" circle color="red" aria-label={`Nieprzeczytane wiadomości: ${all.unreadMessages}`}>
                    {all.unreadMessages}
                  </Badge>
                ) : null
              }
            >
              Wiadomości
            </Tabs.Tab>
          ) : null}
        </Tabs.List>
        <Tabs.Panel value="harmonogram" pt="md">
          {portal.works.length ? (
            <SimpleGrid cols={1} spacing="md">
              <MonthCalendar works={portal.works} colorOf={colorOf} base={files?.base} />
              <WorkList works={portal.works} colorOf={colorOf} unscheduled={unscheduled} base={files?.base} />
            </SimpleGrid>
          ) : (
            <Card withBorder radius="md">
              <Text fw={600}>Terminy prac są w przygotowaniu</Text>
              <Text size="sm" c="dimmed">
                Gdy zaplanujemy prace, pojawią się tutaj w kalendarzu — z informacją, czego dotyczą i kiedy się rozpoczną.
              </Text>
            </Card>
          )}
          <Text size="xs" c="dimmed" mt="md">
            Terminy mogą się zmieniać (pogoda, dostawy, wcześniejsze etapy) — harmonogram aktualizujemy na bieżąco.
          </Text>
        </Tabs.Panel>
        <Tabs.Panel value="sprawy" pt="md">
          <Stack gap="xs">
            {shownCases.length ? (
              shownCases.map((c) => (
                <Card key={c.id} withBorder radius="md" padding="sm">
                  <Group justify="space-between" wrap="wrap" gap="xs" align="flex-start">
                    <div style={{ flex: '1 1 240px' }}>
                      <Text fw={700}>
                        {c.number} — {c.title}
                      </Text>
                      {c.trades.length > 0 && (
                        <Text size="sm" c="dimmed">
                          {c.trades.join(', ')}
                        </Text>
                      )}
                      {c.siteAddress && <Text size="sm">Miejsce prac: {c.siteAddress}</Text>}
                      {c.unscheduled > 0 && (
                        <Text size="xs" c="dimmed">
                          Prace bez ustalonego terminu: {c.unscheduled}
                        </Text>
                      )}
                    </div>
                    <Group gap="xs">
                      <Badge size="sm" variant="light" color={c.closed ? 'gray' : 'blue'}>
                        {c.closed ? 'Zakończona' : 'W toku'}
                      </Badge>
                      {many && !caseId && (
                        <Button size="xs" variant="subtle" onClick={() => setCaseId(c.id)}>
                          Pokaż tylko tę sprawę
                        </Button>
                      )}
                    </Group>
                  </Group>
                </Card>
              ))
            ) : (
              <Text size="sm" c="dimmed">
                Nie masz jeszcze spraw.
              </Text>
            )}
          </Stack>
        </Tabs.Panel>
        <Tabs.Panel value="dokumenty" pt="md">
          <Documents portal={portal} files={files} />
        </Tabs.Panel>
        {portal.protocols?.length ? (
          <Tabs.Panel value="protokoly" pt="md">
            <Protocols protocols={portal.protocols} files={files} />
          </Tabs.Panel>
        ) : null}
        <Tabs.Panel value="finanse" pt="md">
          <FinanceDocuments invoices={portal.invoices} files={files} />
        </Tabs.Panel>
        {files?.base ? (
          <Tabs.Panel value="wiadomosci" pt="md">
            <PortalMessages base={files.base} cases={all.cases} />
          </Tabs.Panel>
        ) : null}
      </Tabs>
    </Stack>
    </CaseNumbers.Provider>
  );
}
